/**
 * Reports module — pulls aggregates over arbitrary date ranges.
 *
 * Range params are RFC3339 (UTC). Each query is keyed on (from, to) so
 * picking a different preset triggers a fresh fetch but switching back to
 * a previously-viewed range hits the cache. We don't poll: reports are
 * "click and look", not live counters.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { getAccessToken } from '@/lib/auth';

export interface SummaryWindow {
  total: number;
  sent: number;
  delivered: number;
  failed: number;
  terminal: number;
  inbound: number;
  delivery_rate: number | null;
  spend: number;
}

export interface SummaryResponse {
  from: string;
  to: string;
  current: SummaryWindow;
  previous: SummaryWindow;
  has_compare: boolean;
}

export interface VolumePoint {
  /** YYYY-MM-DD */
  day: string;
  total: number;
  delivered: number;
  failed: number;
}

export interface VolumeResponse {
  items: VolumePoint[];
}

export interface SenderBreakdownItem {
  sender_value: string;
  sender_id?: number;
  total: number;
  delivered: number;
  failed: number;
  spend: number;
}

export interface CampaignBreakdownItem {
  campaign_id: number;
  campaign_name: string;
  campaign_status: string;
  total: number;
  delivered: number;
  failed: number;
  spend: number;
}

export interface BreakdownSenderResponse {
  items: SenderBreakdownItem[];
}

export interface BreakdownCampaignResponse {
  items: CampaignBreakdownItem[];
}

export interface ReportRange {
  /** RFC3339 inclusive lower bound. */
  from: string;
  /** RFC3339 exclusive upper bound. Defaults to now if omitted server-side. */
  to: string;
}

const REPORTS_ROOT = ['reports'] as const;

// Reseller-scoped earnings report. Lives at /v1/admin/reports/earnings
// (RequireReseller-gated) — distinct from the customer-facing
// /v1/reports/* endpoints because the data scope is "my children's
// traffic" not "my own messages".
export interface EarningsTotals {
  sent: number;
  delivered: number;
  revenue: number;
  cost: number;
  earnings: number;
  active_customers: number;
}
export interface EarningsMonthRow {
  /** RFC3339 timestamp at the first of the month (UTC). */
  month: string;
  sent: number;
  delivered: number;
  revenue: number;
  cost: number;
  earnings: number;
  active_customers: number;
}
export interface EarningsCustomerRow {
  customer_id: number;
  uuid: string;
  name: string;
  country?: string;
  sent: number;
  delivered: number;
  revenue: number;
  cost: number;
  earnings: number;
}
export interface EarningsResponse {
  from: string;
  to: string;
  totals: EarningsTotals;
  by_month: EarningsMonthRow[];
  customers: EarningsCustomerRow[];
}

export function useResellerEarnings(range: ReportRange) {
  return useQuery({
    queryKey: [...REPORTS_ROOT, 'earnings', range.from, range.to],
    queryFn: () =>
      api.get<EarningsResponse>(
        `/v1/admin/reports/earnings?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
      ),
    staleTime: 60_000,
  });
}

export function useReportSummary(range: ReportRange) {
  return useQuery({
    queryKey: [...REPORTS_ROOT, 'summary', range.from, range.to],
    queryFn: () =>
      api.get<SummaryResponse>(
        `/v1/reports/summary?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
      ),
    staleTime: 60_000,
  });
}

export function useReportVolume(range: ReportRange) {
  return useQuery({
    queryKey: [...REPORTS_ROOT, 'volume', range.from, range.to],
    queryFn: () =>
      api.get<VolumeResponse>(
        `/v1/reports/volume?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
      ),
    staleTime: 60_000,
  });
}

export function useReportBreakdownBySender(range: ReportRange, limit = 10) {
  return useQuery({
    queryKey: [...REPORTS_ROOT, 'breakdown', 'sender', range.from, range.to, limit],
    queryFn: () =>
      api.get<BreakdownSenderResponse>(
        `/v1/reports/breakdown?by=sender&limit=${limit}&from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
      ),
    staleTime: 60_000,
  });
}

export function useReportBreakdownByCampaign(range: ReportRange, limit = 10) {
  return useQuery({
    queryKey: [...REPORTS_ROOT, 'breakdown', 'campaign', range.from, range.to, limit],
    queryFn: () =>
      api.get<BreakdownCampaignResponse>(
        `/v1/reports/breakdown?by=campaign&limit=${limit}&from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
      ),
    staleTime: 60_000,
  });
}

/**
 * Triggers a CSV download for the messages in the given range.
 *
 * We can't use a plain `<a href>` because the endpoint requires the
 * Authorization header, and the browser doesn't attach it to anchor
 * clicks. Instead we fetch as blob, build an object URL and click a
 * synthetic anchor — same UX as a direct download link, but with auth.
 */
export async function downloadReportCSV(range: ReportRange): Promise<void> {
  const apiBase =
    (typeof import.meta.env.VITE_API_BASE_URL === 'string' &&
      import.meta.env.VITE_API_BASE_URL) ||
    'http://localhost:8080';
  const url = `${apiBase}/v1/reports/messages.csv?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
  const token = getAccessToken();
  const response = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`CSV export failed (${response.status})`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = `ezsender-messages-${range.from.slice(0, 10)}_${range.to.slice(0, 10)}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke after a tick so the click handler can finish.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
